<template>
    <div class="study-log-entry-panel" :class="{'is-open': isOpen}">
        <div class="panel-overlay" @click="close"></div>
        <div class="panel-content">
            <div class="panel-header">
                <h3><i class="fad fa-clipboard-list-check fa-fw mr-2"></i>Log Study Activity</h3>
                <button class="delete is-medium" @click="close"></button>
            </div>

            <div class="panel-body">
                <p class="mb-4 has-text-grey">Manually record offline study time or other qualifying activities.</p>

                <!-- Date -->
                <b-field label="Date" label-position="inside">
                    <b-datepicker
                        v-model="form.date"
                        placeholder="Select date"
                        icon="calendar"
                        :max-date="maxDate"
                        :date-formatter="formatDate"
                        trap-focus>
                    </b-datepicker>
                </b-field>

                <!-- Time Range -->
                <div class="columns">
                    <div class="column">
                        <b-field label="Start Time" label-position="inside">
                            <b-timepicker
                                v-model="form.startTime"
                                placeholder="Start time"
                                icon="clock"
                                :increment-minutes="15"
                                hour-format="12">
                            </b-timepicker>
                        </b-field>
                    </div>
                    <div class="column">
                        <b-field label="End Time" label-position="inside">
                            <b-timepicker
                                v-model="form.endTime"
                                placeholder="End time"
                                icon="clock"
                                :increment-minutes="15"
                                hour-format="12">
                            </b-timepicker>
                        </b-field>
                    </div>
                </div>

                <!-- Session Duration Display -->
                <div class="session-duration mb-4" v-if="sessionDuration !== null">
                    <b-tag :type="durationTagClass">
                        <i class="far fa-stopwatch mr-2 has-text-white"></i>
                        <strong class="has-text-white">{{ sessionDurationDisplay }}</strong>
                    </b-tag>
                    <span class="has-text-danger is-size-7 ml-2" v-if="hasConflict">
                        <i class="fas fa-exclamation-triangle mr-1"></i>Conflicts with existing entry
                    </span>
                    <span class="has-text-danger is-size-7 ml-2" v-else-if="sessionDuration > 240">
                        Maximum 4 hours allowed
                    </span>
                    <span class="has-text-danger is-size-7 ml-2" v-else-if="sessionDuration <= 0">
                        End time must be after start time
                    </span>
                </div>

                <!-- Activity/Objective Selection -->
                <b-field label="Activity/Objective" label-position="inside">
                    <b-select v-model="form.activity" placeholder="Select activity" expanded>
                        <optgroup v-if="objectives.length > 0" label="Your Objectives">
                            <option v-for="(obj, i) in objectives" :key="'obj-'+i" :value="obj">{{ obj }}</option>
                        </optgroup>
                        <optgroup label="Other Activities">
                            <option value="Offline Study">Offline Study</option>
                            <option value="Reading">Reading</option>
                            <option value="Practice">Practice</option>
                            <option value="Research">Research</option>
                            <option value="Other">Other</option>
                        </optgroup>
                    </b-select>
                </b-field>

                <!-- Other Activity Text Input -->
                <b-field v-if="form.activity === 'Other'" label="Describe Activity" label-position="inside">
                    <b-input v-model="form.otherActivity" placeholder="e.g., Watching supplementary videos"></b-input>
                </b-field>

                <!-- Error Message -->
                <b-message type="is-danger" v-if="errorMessage" class="mt-4">
                    {{ errorMessage }}
                </b-message>
            </div>

            <div class="panel-footer">
                <b-button type="is-light" @click="close">Cancel</b-button>
                <b-button type="is-success" @click="submit" :loading="isSubmitting" :disabled="!isValid">
                    <i class="fas fa-plus mr-2"></i>Add Entry
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import {client as http} from "@/http_client";

export default {
    name: 'StudyLogEntryPanel',
    props: {
        isOpen: {
            type: Boolean,
            default: false
        },
        enrollmentId: {
            type: [Number, String],
            required: true
        },
        objectives: {
            type: Array,
            default: () => []
        },
        preselectedActivity: {
            type: String,
            default: null
        },
        existingEntries: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            form: {
                date: new Date(),
                startTime: null,
                endTime: null,
                activity: null,
                otherActivity: ''
            },
            isSubmitting: false,
            errorMessage: null,
            maxDate: new Date()
        };
    },
    computed: {
        sessionDuration() {
            if (!this.form.startTime || !this.form.endTime) return null;

            const start = moment(this.form.startTime);
            const end = moment(this.form.endTime);

            // Handle case where end time is before start (next day scenario not allowed)
            return end.diff(start, 'minutes');
        },
        sessionDurationDisplay() {
            if (this.sessionDuration === null) return '';
            const hours = Math.floor(Math.abs(this.sessionDuration) / 60);
            const mins = Math.abs(this.sessionDuration) % 60;
            if (hours > 0) {
                return `${hours}h ${mins}m`;
            }
            return `${mins} minutes`;
        },
        // Build full start/end datetime from date + time
        fullStartDateTime() {
            if (!this.form.date || !this.form.startTime) return null;
            return this.buildDateTime(this.form.date, this.form.startTime);
        },
        fullEndDateTime() {
            if (!this.form.date || !this.form.endTime) return null;
            return this.buildDateTime(this.form.date, this.form.endTime);
        },
        // Check for conflicts with existing entries
        hasConflict() {
            if (!this.fullStartDateTime || !this.fullEndDateTime) return false;
            if (this.sessionDuration <= 0) return false;

            const newStart = this.fullStartDateTime.valueOf();
            const newEnd = this.fullEndDateTime.valueOf();

            for (const entry of this.existingEntries) {
                const entryStart = moment(entry.start_date).valueOf();
                const entryEnd = moment(entry.end_date).valueOf();

                // Check if ranges overlap
                if ((newStart < entryEnd && newEnd > entryStart)) {
                    return true;
                }
            }
            return false;
        },
        durationTagClass() {
            if (this.hasConflict || this.sessionDuration > 240 || this.sessionDuration <= 0) {
                return 'is-danger';
            }
            return 'is-success';
        },
        isValid() {
            if (!this.form.date || !this.form.startTime || !this.form.endTime) return false;
            if (!this.form.activity) return false;
            if (this.form.activity === 'Other' && !this.form.otherActivity.trim()) return false;
            if (this.sessionDuration === null || this.sessionDuration <= 0 || this.sessionDuration > 240) return false;
            if (this.hasConflict) return false;
            return true;
        }
    },
    methods: {
        formatDate(date) {
            return moment(date).format('MMMM D, YYYY');
        },
        close() {
            this.$emit('close');
        },
        resetForm() {
            this.form = {
                date: new Date(),
                startTime: null,
                endTime: null,
                activity: this.preselectedActivity || null,
                otherActivity: ''
            };
            this.errorMessage = null;
        },
        buildDateTime(date, time) {
            const d = moment(date);
            const t = moment(time);
            return moment({
                year: d.year(),
                month: d.month(),
                date: d.date(),
                hour: t.hour(),
                minute: t.minute(),
                second: 0
            });
        },
        async submit() {
            if (!this.isValid) return;
            if (!this.enrollmentId) {
                this.errorMessage = 'No enrollment selected';
                return;
            }

            this.isSubmitting = true;
            this.errorMessage = null;

            try {
                const startDateTime = this.buildDateTime(this.form.date, this.form.startTime);
                const endDateTime = this.buildDateTime(this.form.date, this.form.endTime);

                const payload = {
                    start_date: startDateTime.toISOString(),
                    end_date: endDateTime.toISOString(),
                    activity: this.form.activity,
                    other_activity: this.form.activity === 'Other' ? this.form.otherActivity : null
                };

                await http.post(`app/enrollments/${this.enrollmentId}/studylog`, payload, {force: true});

                this.$buefy.toast.open({
                    message: 'Study log entry added successfully',
                    type: 'is-success'
                });

                this.resetForm();
                this.$emit('added');
            } catch (err) {
                console.error('Error adding study log entry:', err);
                this.errorMessage = err.response?.data || 'Failed to add entry. Please try again.';
            } finally {
                this.isSubmitting = false;
            }
        }
    },
    watch: {
        isOpen(newVal) {
            if (newVal) {
                this.resetForm();
            }
        },
        preselectedActivity: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.form.activity = newVal;
                }
            }
        },
        // Auto-set end time to 1 hour after start time
        'form.startTime'(newVal) {
            if (newVal && !this.form.endTime) {
                const endTime = new Date(newVal.getTime());
                endTime.setHours(endTime.getHours() + 1);
                this.form.endTime = endTime;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.study-log-entry-panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 100;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;

    &.is-open {
        pointer-events: auto;
        opacity: 1;

        .panel-content {
            transform: translateX(0);
        }
    }

    .panel-overlay {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background: rgba(0, 0, 0, 0.5);
    }

    .panel-content {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 450px;
        max-width: 100%;
        background: white;
        box-shadow: -5px 0 20px rgba(0, 0, 0, 0.2);
        display: flex;
        flex-direction: column;
        transform: translateX(100%);
        transition: transform 0.3s ease;
    }

    .panel-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1.25rem 1.5rem;
        border-bottom: 1px solid #e5e5e5;
        background: #f8f8f8;

        h3 {
            margin: 0;
            font-size: 1.1rem;
            font-weight: 600;
        }
    }

    .panel-body {
        flex: 1;
        padding: 1.5rem;
        overflow-y: auto;
    }

    .panel-footer {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
        padding: 1rem 1.5rem;
        border-top: 1px solid #e5e5e5;
        background: #f8f8f8;
    }

    .session-duration {
        display: flex;
        align-items: center;
    }
}

// Responsive adjustments
@media (max-width: 500px) {
    .study-log-entry-panel .panel-content {
        width: 100%;
    }
}
</style>
